import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { 
  AlternateWaterCapacityRequest, 
  AlternateWaterCapacityResponse,
  AlternateWaterCapacityDetailRequest,
  AlternateWaterCapacityDetailResponse,
  AlternateWaterCapacityDropdownRequest,
  AlternateWaterCapacityDropdownApiResponse,
  AlternateWaterCapacity,
  SaveAlternateWaterCapacityRequest,
  DeleteAlternateWaterCapacityRequest,
  AlternateWaterCapacityRow,
  DropdownColumn,
  AlternateWaterCapacityFilterResponse

} from './interfaces/alternate-water-capacity.interfaces';
import { ErrorHandler } from '../utils/error-handler';
import { environment } from '../environments/environments';

@Injectable({
  providedIn: 'root',
})
export class AlternateWaterCapacityService {
  private baseUrl = environment.WebApiURL;
  private readonly alternateWaterCapacityExportTabName = 'WaterAlternateCapacities';

  private static readonly DEFAULT_ALTERNATE_CAPACITY_TYPES: string[] = ['MVC(BBLs)', '%'];

  private dropdownUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterAlternateDropdowns`;
  }

  private filterOptionsUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterAlternateMultiFilter`;
  }

  private capacitiesUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/GetWaterAlternateCapacitiesDetails`;
  }

  private saveCapacitiesUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/InsertUpdateWaterAlternateCapacity`;
  }

  private deleteCapacitiesUrl(): string {
    return `${this.baseUrl}/api/WaterCapacities/DeleteWaterAlternateCapacities`;
  }

  private exportUrl(): string {
    return `${this.baseUrl}/api/FlowNetworkFE/ExportAllTabsData`;
  }

  constructor(private http: HttpClient) {}

  async exportAllAlternateWaterCapacitiesData(): Promise<{ blob: Blob; fileName: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const httpResponse = await firstValueFrom(
      this.http.post(this.exportUrl(), { tabName: this.alternateWaterCapacityExportTabName }, {
        headers,
        observe: 'response' as const,
        responseType: 'blob' as const,
      })
    );

    const blob = httpResponse.body;
    if (!blob) {
      throw new Error('Export returned an empty file.');
    }

    const contentDisposition = httpResponse.headers.get('content-disposition') || '';
    const contentType = httpResponse.headers.get('content-type') || blob.type || '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    const rawHeaderFileName = fileNameMatch?.[1]?.trim() || '';
    const decodedHeaderFileName = decodeURIComponent(rawHeaderFileName.replace(/^"|"$/g, ''));
    const normalizedContentType = contentType.toLowerCase();
    const inferredExtension =
      normalizedContentType.includes('spreadsheetml') ||
      normalizedContentType.includes('xlsx') ||
      decodedHeaderFileName.toLowerCase().endsWith('.xlsx')
        ? '.xlsx'
        : '.csv';
    const fallbackFileName = `alternate-water-capacities${inferredExtension}`;
    const decodedFileName = rawHeaderFileName ? decodedHeaderFileName : fallbackFileName;
    const fileName = /\.[A-Za-z0-9]+$/.test(decodedFileName)
      ? decodedFileName
      : `${decodedFileName}${inferredExtension}`;

    return { blob, fileName };
  }

  private normalizeText(value: string | null | undefined): string {
    return (value ?? '').trim();
  }

  private normalizeDateForInput(value: string | null | undefined): string {
    const raw = this.normalizeText(value);
    if (!raw) {
      return '';
    }

    const datePart = raw.includes('T') ? raw.split('T')[0] : raw;
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }

    const parsed = new Date(raw);
    if (!isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }

    return '';
  }

  private toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private mapApiItemToAlternateWaterCapacity(item: any): AlternateWaterCapacity {
    return {
      devArea: this.normalizeText(item?.developmentArea ?? item?.devArea),
      feAssetName: this.normalizeText(item?.feAssetName),
      disposalOutlet: this.normalizeText(item?.disposalOutletName ?? item?.disposalOutlet),
      alternativeCapacityType: this.normalizeText(item?.alternateCapacityType ?? item?.alternativeCapacityType),
      alternativeCapacityValue: this.toNumberOrNull(item?.alternateLimitQty ?? item?.alternativeCapacityValue ?? item?.alternateCapacityValue),
      effectiveDate: this.normalizeDateForInput(item?.effectiveDate),
      isFloating: this.normalizeText(item?.isFloating) || 'N',
      isActive: this.normalizeText(item?.isActive) || 'Y',
      priority: this.toNumberOrNull(item?.priority)
    };
  }

  private parseDropdownResponse(response: AlternateWaterCapacityDropdownApiResponse): AlternateWaterCapacityResponse {
    const columns = response?.dropDownItem || [];

    const findColumn = (name: string) =>
      columns.find(item => (item.columnName || '').toLowerCase() === name.toLowerCase());

    const devAreaItem = findColumn('DevelopmentArea') || findColumn('DevArea');
    const feAssetNameItem = findColumn('FEAssetName') || findColumn('FeAssetName');
    const disposalOutletItem = findColumn('DisposalOutletName') || findColumn('DisposalOutlet');
    const alternativeCapacityTypeItem = findColumn('AlternativeCapacityType') || findColumn('AlternateCapacityType');

    return {
      devAreas: devAreaItem?.values || [],
      feAssetNames: feAssetNameItem?.values || [],
      disposalOutlets: disposalOutletItem?.values || [],
      alternativeCapacityTypes: alternativeCapacityTypeItem?.values?.length
        ? alternativeCapacityTypeItem.values
        : [...AlternateWaterCapacityService.DEFAULT_ALTERNATE_CAPACITY_TYPES],
    };
  }
  private parseFilterResponse(response: AlternateWaterCapacityFilterResponse): AlternateWaterCapacityResponse {
    const columns = response?.multifilteritem || [];

    const findColumn = (name: string) =>
      columns.find(item => (item.columnName || '').toLowerCase() === name.toLowerCase());

    const devAreaItem = findColumn('DevelopmentArea') || findColumn('DevArea');
    const feAssetNameItem = findColumn('FEAssetName') || findColumn('FeAssetName');
    const disposalOutletItem = findColumn('DisposalOutletName') || findColumn('DisposalOutlet');
    const alternativeCapacityTypeItem = findColumn('AlternativeCapacityType') || findColumn('AlternateCapacityType');

    return {
      devAreas: devAreaItem?.values || [],
      feAssetNames: feAssetNameItem?.values || [],
      disposalOutlets: disposalOutletItem?.values || [],
      alternativeCapacityTypes: alternativeCapacityTypeItem?.values?.length
        ? alternativeCapacityTypeItem.values
        : [...AlternateWaterCapacityService.DEFAULT_ALTERNATE_CAPACITY_TYPES],
    };
  }

  async getAllDropdownOptions(filters?: Partial<AlternateWaterCapacityDropdownRequest>): Promise<AlternateWaterCapacityResponse> {
    try {
      const requestBody: AlternateWaterCapacityDropdownRequest = {
        developmentArea: filters?.developmentArea || null,
        feAssetName: filters?.feAssetName || null,
        disposalOutletName: filters?.disposalOutletName || null,
      };

      const response = await firstValueFrom(
        this.http.post<AlternateWaterCapacityDropdownApiResponse>(this.dropdownUrl(), requestBody)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      if (parsed.success) {
        return this.parseDropdownResponse(response);
      }

      return { devAreas: [], feAssetNames: [], disposalOutlets: [], alternativeCapacityTypes: [] };
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateWaterCapacityService.getAllDropdownOptions');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async getDropdownOptions(requestBody: Partial<AlternateWaterCapacityDropdownRequest>): Promise<AlternateWaterCapacityResponse> {
    return this.getAllDropdownOptions(requestBody);
  }

  async getDevAreas(): Promise<string[]> {
    try {
      const dropdownData = await this.getAllDropdownOptions();
      return dropdownData.devAreas;
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateWaterCapacityService.getDevAreas');
      return [];
    }
  }

  async getFeAssetNames(): Promise<string[]> {
    try {
      const dropdownData = await this.getAllDropdownOptions();
      return dropdownData.feAssetNames;
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateWaterCapacityService.getFeAssetNames');
      return [];
    }
  }

  async getDisposalOutlets(): Promise<string[]> {
    try {
      const dropdownData = await this.getAllDropdownOptions();
      return dropdownData.disposalOutlets;
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateWaterCapacityService.getDisposalOutlets');
      return [];
    }
  }

  async getAlternativeCapacityTypes(): Promise<string[]> {
    try {
      const dropdownData = await this.getAllDropdownOptions();
      return dropdownData.alternativeCapacityTypes;
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateWaterCapacityService.getAlternativeCapacityTypes');
      return [];
    }
  }

  async getFeAssetNamesByDevArea(devArea: string): Promise<string[]> {
    try {
      const dropdownData = await this.getAllDropdownOptions({ developmentArea: devArea });
      return dropdownData.feAssetNames;
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateWaterCapacityService.getFeAssetNamesByDevArea');
      return [];
    }
  }

  async getDisposalOutletsByDevArea(devArea: string): Promise<string[]> {
    try {
      const dropdownData = await this.getAllDropdownOptions({ developmentArea: devArea });
      return dropdownData.disposalOutlets;
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateWaterCapacityService.getDisposalOutletsByDevArea');
      return [];
    }
  }

  async getCapacities(filters?: Partial<AlternateWaterCapacityDetailRequest>): Promise<AlternateWaterCapacity[]> {
    try {
      const paged = await this.getCapacitiesWithPagination(filters);
      return paged.capacities;
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateWaterCapacityService.getCapacities');
      return [];
    }
  }

  async getCapacitiesWithPagination(filters?: Partial<AlternateWaterCapacityDetailRequest>): Promise<{
    capacities: AlternateWaterCapacity[];
    nextRowNumber: number | null;
  }> {
    try {
      const payload = {
        developmentArea: filters?.developmentArea || null,
        feAssetName: filters?.feAssetName || null,
        disposalOutletName: filters?.disposalOutletName || null,
        startDate: filters?.startDate || null,
        endDate: filters?.endDate || null,
        lastRowNumber: filters?.lastRowNumber ?? null
      };

      const response = await firstValueFrom(
        this.http.post<AlternateWaterCapacityDetailResponse>(this.capacitiesUrl(), payload)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      if (parsed.success) {
        return {
          capacities: (response?.waterAlternateCapacityItems || []).map((item: any) => this.mapApiItemToAlternateWaterCapacity(item)),
          nextRowNumber: response?.nextRowNumber ?? null
        };
      }

      return {
        capacities: [],
        nextRowNumber: null
      };
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateWaterCapacityService.getCapacitiesWithPagination');
      return {
        capacities: [],
        nextRowNumber: null
      };
    }
  }

  async getFilterOptionsBySelection(filters?: Partial<AlternateWaterCapacityDropdownRequest>): Promise<AlternateWaterCapacityResponse> {
    try {
      const requestBody: AlternateWaterCapacityDropdownRequest = {
        developmentArea: filters?.developmentArea || null,
        feAssetName: filters?.feAssetName || null,
        disposalOutletName: filters?.disposalOutletName || null,
      };

      const response = await firstValueFrom(
        this.http.post<AlternateWaterCapacityFilterResponse>(this.filterOptionsUrl(), requestBody)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      if (parsed.success) {
        return this.parseFilterResponse(response);
      }

      return { devAreas: [], feAssetNames: [], disposalOutlets: [], alternativeCapacityTypes: [] };
    } catch (error) {
      ErrorHandler.handleError(error, 'AlternateWaterCapacityService.getFilterOptionsBySelection');
      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async saveCapacity(row: AlternateWaterCapacityRow, action: 'INSERT' | 'UPDATE', userName: string, originalCapacity?: AlternateWaterCapacityRow): Promise<any> {
    try {
      const body: SaveAlternateWaterCapacityRequest = {
        action: action,
        developmentArea: row.devArea || '',
        feAssetName: row.feAssetName || '',
        disposalOutletName: row.disposalOutlet || '',
        alternateCapacityType: row.alternativeCapacityType || '',
        alternateLimitQty: row.alternativeCapacityValue ?? 0,
        effectiveDate: row.effectiveDate || '',
        userName: userName
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.saveCapacitiesUrl(), body)
      );

      const parsed = ErrorHandler.parseApiResult(response);
      const nestedStatus = response?.statusMessage;
      const nestedStatusCode =
        nestedStatus && typeof nestedStatus === 'object' ? nestedStatus?.statusCode : undefined;
      const nestedStatusMessage =
        nestedStatus && typeof nestedStatus === 'object' ? nestedStatus?.statusMessage : undefined;

      const statusCode = Number(nestedStatusCode ?? response?.statusCode ?? parsed.statusCode ?? 500);
      const statusMessage =
        nestedStatusMessage ||
        (typeof response?.statusMessage === 'string' ? response.statusMessage : null) ||
        parsed.message ||
        'Unknown error occurred';

      return {
        ...response,
        statusCode,
        statusMessage,
        error: statusCode !== 200,
      };
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  async saveMultipleCapacities(capacities: AlternateWaterCapacityRow[], userName: string): Promise<any[]> {
    try {
      const results: any[] = [];
      
      for (const capacity of capacities) {
        const action = capacity.isNew ? 'INSERT' : 'UPDATE';
        const originalCapacity = capacity.original as AlternateWaterCapacityRow;
        
        const result = await this.saveCapacity(capacity, action, userName, originalCapacity);
        results.push(result);
      }

      return results;
    } catch (error: any) {
      ErrorHandler.handleError(error, 'AlternateWaterCapacityService.saveMultipleCapacities');

      const errorMessage = ErrorHandler.extractApiErrorMessage(error);
      const customError = new Error(errorMessage);
      (customError as any).originalError = error;
      throw customError;
    }
  }

  async deleteCapacity(row: AlternateWaterCapacityRow, userName: string): Promise<any> {
    if (!row.devArea || !row.feAssetName || !row.disposalOutlet || !userName) {
      return {
        statusCode: 400,
        statusMessage: 'developmentArea, feAssetName, disposalOutletName and userName are required for deletion.',
        error: true,
      };
    }

    try {
      const body: DeleteAlternateWaterCapacityRequest = {
        developmentArea: row.devArea,
        feAssetName: row.feAssetName,
        disposalOutletName: row.disposalOutlet,
        userName: userName
      };

      const response = await firstValueFrom(
        this.http.post<any>(this.deleteCapacitiesUrl(), body)
      );

      if (typeof response === 'string') {
        return {
          statusCode: 200,
          statusMessage: response,
          error: false,
        };
      }

      const parsed = ErrorHandler.parseApiResult(response);
      return {
        statusCode: parsed.statusCode,
        statusMessage: parsed.message,
        error: !parsed.success,
        ...response,
      };
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      return {
        statusCode: parsed.statusCode || error?.status || 500,
        statusMessage: parsed.message,
        error: true,
      };
    }
  }

  async deleteMultipleCapacities(capacities: AlternateWaterCapacityRow[], userName: string): Promise<any[]> {
    const results: any[] = [];
    
    for (const capacity of capacities) {
      if (capacity.isNew) {
        continue;
      }
      
      const result = await this.deleteCapacity(capacity, userName);
      results.push(result);
    }

    return results;
  }
}

export type { 
  AlternateWaterCapacityRequest,
  AlternateWaterCapacityResponse,
  AlternateWaterCapacityDetailRequest,
  AlternateWaterCapacityDetailResponse,
  AlternateWaterCapacity,
  SaveAlternateWaterCapacityRequest,
  DeleteAlternateWaterCapacityRequest,
  AlternateWaterCapacityRow,
  DropdownColumn
} from './interfaces/alternate-water-capacity.interfaces';